import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Building, Users, ArrowLeft, Sun, Moon } from 'lucide-react';

import { useDepartments } from '../hooks/useDepartments';
const ROLES = ['Admin', 'Coordinator', 'Member'];

const DARK_LOGO = 'https://customer-assets.emergentagent.com/job_uni-work/artifacts/5oxvo39p_United%20Group%20White%20logo%20PNG%20%281%29.png';
const LIGHT_LOGO = 'https://customer-assets.emergentagent.com/job_uni-work/artifacts/eh7i5y2n_United%20Group%20PNG.png';

// Animated Kanban Board SVG
const KanbanBoard = () => (
  <div className="login-kanban">
    <svg viewBox="0 0 400 260" fill="none" className="w-full max-w-[380px]">
      {/* Board frame */}
      <rect x="10" y="10" width="380" height="240" rx="12" fill="rgba(255,255,255,0.04)" stroke="rgba(245,166,35,0.15)" strokeWidth="1"/>
      
      {/* Column headers */}
      <rect x="22" y="22" width="110" height="28" rx="6" fill="rgba(100,116,139,0.3)"/>
      <text x="77" y="41" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10" fontWeight="600">To Do</text>
      
      <rect x="145" y="22" width="110" height="28" rx="6" fill="rgba(59,130,246,0.25)"/>
      <text x="200" y="41" textAnchor="middle" fill="rgba(147,197,253,0.9)" fontSize="10" fontWeight="600">In Progress</text>
      
      <rect x="268" y="22" width="110" height="28" rx="6" fill="rgba(34,197,94,0.2)"/>
      <text x="323" y="41" textAnchor="middle" fill="rgba(134,239,172,0.9)" fontSize="10" fontWeight="600">Completed</text>
      
      {/* To Do cards */}
      <rect x="22" y="60" width="110" height="42" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" className="login-card-anim login-card-1"/>
      <rect x="28" y="68" width="50" height="4" rx="2" fill="rgba(245,166,35,0.5)"/>
      <rect x="28" y="78" width="80" height="3" rx="1.5" fill="rgba(255,255,255,0.15)"/>
      <rect x="28" y="86" width="60" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
      <circle cx="120" cy="76" r="8" fill="rgba(239,68,68,0.2)"/>
      <text x="120" y="79" textAnchor="middle" fill="rgba(239,68,68,0.7)" fontSize="7" fontWeight="700">1</text>
      
      <rect x="22" y="110" width="110" height="42" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" className="login-card-anim login-card-2"/>
      <rect x="28" y="118" width="65" height="4" rx="2" fill="rgba(245,166,35,0.4)"/>
      <rect x="28" y="128" width="75" height="3" rx="1.5" fill="rgba(255,255,255,0.15)"/>
      <rect x="28" y="136" width="55" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
      <circle cx="120" cy="126" r="8" fill="rgba(234,179,8,0.2)"/>
      <text x="120" y="129" textAnchor="middle" fill="rgba(234,179,8,0.7)" fontSize="7" fontWeight="700">5</text>
      
      <rect x="22" y="160" width="110" height="42" rx="6" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" className="login-card-anim login-card-3"/>
      <rect x="28" y="168" width="45" height="4" rx="2" fill="rgba(245,166,35,0.3)"/>
      <rect x="28" y="178" width="70" height="3" rx="1.5" fill="rgba(255,255,255,0.12)"/>
      <rect x="28" y="186" width="48" height="3" rx="1.5" fill="rgba(255,255,255,0.08)"/>
      
      {/* In Progress cards */}
      <rect x="145" y="60" width="110" height="42" rx="6" fill="rgba(59,130,246,0.08)" stroke="rgba(59,130,246,0.15)" strokeWidth="0.5" className="login-card-anim login-card-4"/>
      <rect x="151" y="68" width="55" height="4" rx="2" fill="rgba(59,130,246,0.5)"/>
      <rect x="151" y="78" width="85" height="3" rx="1.5" fill="rgba(255,255,255,0.15)"/>
      <rect x="151" y="86" width="65" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
      {/* Progress bar */}
      <rect x="151" y="94" width="90" height="3" rx="1.5" fill="rgba(255,255,255,0.06)"/>
      <rect x="151" y="94" width="54" height="3" rx="1.5" fill="rgba(59,130,246,0.5)" className="login-progress-anim"/>
      
      <rect x="145" y="110" width="110" height="42" rx="6" fill="rgba(59,130,246,0.06)" stroke="rgba(59,130,246,0.12)" strokeWidth="0.5" className="login-card-anim login-card-5"/>
      <rect x="151" y="118" width="70" height="4" rx="2" fill="rgba(59,130,246,0.4)"/>
      <rect x="151" y="128" width="78" height="3" rx="1.5" fill="rgba(255,255,255,0.12)"/>
      <rect x="151" y="136" width="58" height="3" rx="1.5" fill="rgba(255,255,255,0.08)"/>
      <rect x="151" y="144" width="90" height="3" rx="1.5" fill="rgba(255,255,255,0.06)"/>
      <rect x="151" y="144" width="36" height="3" rx="1.5" fill="rgba(59,130,246,0.4)" className="login-progress-anim-2"/>
      
      {/* Completed cards */}
      <rect x="268" y="60" width="110" height="42" rx="6" fill="rgba(34,197,94,0.06)" stroke="rgba(34,197,94,0.12)" strokeWidth="0.5" className="login-card-anim login-card-6"/>
      <rect x="274" y="68" width="60" height="4" rx="2" fill="rgba(34,197,94,0.4)"/>
      <rect x="274" y="78" width="82" height="3" rx="1.5" fill="rgba(255,255,255,0.12)"/>
      <rect x="274" y="86" width="55" height="3" rx="1.5" fill="rgba(255,255,255,0.08)"/>
      {/* Checkmark */}
      <circle cx="366" cy="76" r="8" fill="rgba(34,197,94,0.2)"/>
      <path d="M362 76l3 3 5-6" stroke="rgba(34,197,94,0.7)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      
      <rect x="268" y="110" width="110" height="42" rx="6" fill="rgba(34,197,94,0.04)" stroke="rgba(34,197,94,0.1)" strokeWidth="0.5" className="login-card-anim login-card-7"/>
      <rect x="274" y="118" width="48" height="4" rx="2" fill="rgba(34,197,94,0.35)"/>
      <rect x="274" y="128" width="76" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
      <rect x="274" y="136" width="60" height="3" rx="1.5" fill="rgba(255,255,255,0.07)"/>
      <circle cx="366" cy="126" r="8" fill="rgba(34,197,94,0.15)"/>
      <path d="M362 126l3 3 5-6" stroke="rgba(34,197,94,0.5)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      
      <rect x="268" y="160" width="110" height="42" rx="6" fill="rgba(34,197,94,0.03)" stroke="rgba(34,197,94,0.08)" strokeWidth="0.5" className="login-card-anim login-card-8"/>
      <rect x="274" y="168" width="55" height="4" rx="2" fill="rgba(34,197,94,0.3)"/>
      <rect x="274" y="178" width="70" height="3" rx="1.5" fill="rgba(255,255,255,0.08)"/>
      <circle cx="366" cy="176" r="8" fill="rgba(34,197,94,0.12)"/>
      <path d="M362 176l3 3 5-6" stroke="rgba(34,197,94,0.4)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>

      {/* Moving task arrow animation hint */}
      <path d="M135 80 L142 80" stroke="rgba(245,166,35,0.4)" strokeWidth="1" strokeDasharray="2 2" className="login-arrow-anim"/>
    </svg>
  </div>
);

// Mini chart SVG
const MiniChart = () => (
  <svg viewBox="0 0 160 60" fill="none" className="w-full max-w-[160px]">
    <rect x="10" y="40" width="16" height="16" rx="2" fill="rgba(34,197,94,0.4)" className="login-bar-anim login-bar-1"/>
    <rect x="32" y="28" width="16" height="28" rx="2" fill="rgba(59,130,246,0.4)" className="login-bar-anim login-bar-2"/>
    <rect x="54" y="15" width="16" height="41" rx="2" fill="rgba(245,166,35,0.5)" className="login-bar-anim login-bar-3"/>
    <rect x="76" y="22" width="16" height="34" rx="2" fill="rgba(59,130,246,0.35)" className="login-bar-anim login-bar-4"/>
    <rect x="98" y="8" width="16" height="48" rx="2" fill="rgba(34,197,94,0.5)" className="login-bar-anim login-bar-5"/>
    <rect x="120" y="18" width="16" height="38" rx="2" fill="rgba(245,166,35,0.4)" className="login-bar-anim login-bar-6"/>
    {/* Trend line */}
    <path d="M18 42 L40 30 L62 17 L84 24 L106 10 L128 20" stroke="rgba(245,166,35,0.6)" strokeWidth="1.5" fill="none" strokeLinecap="round" className="login-line-anim"/>
  </svg>
);

const FeatureItem = ({ title, desc, delay }) => (
  <div className="flex items-start gap-3 login-feature-item" style={{ animationDelay: `${delay}s` }}>
    <div className="w-1.5 h-1.5 rounded-full bg-[#F5A623] mt-2 shrink-0" />
    <div>
      <p className="text-white/90 text-sm font-medium">{title}</p>
      <p className="text-white/40 text-xs mt-0.5">{desc}</p>
    </div>
  </div>
);

const CountUp = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.max(1, Math.floor(end / 30));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 40);
    return () => clearInterval(timer);
  }, [end]);
  return <span>{count}{suffix}</span>;
};

export const LoginPage = () => {
  const DEPARTMENTS = useDepartments();
  const navigate = useNavigate();
  const { login, signup, forgotPassword, verifyOTP, resetPassword } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotStep, setForgotStep] = useState('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(true);
  const [signupData, setSignupData] = useState({
    email: '', password: '', full_name: '', department: '', role: 'Member',
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password, rememberMe);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupData.email.endsWith('@united-group.in')) {
      toast.error('Email must be from @united-group.in domain');
      return;
    }
    setLoading(true);
    try {
      await signup(signupData);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.endsWith('@united-group.in')) {
      toast.error('Email must be from @united-group.in domain');
      return;
    }
    setLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success('OTP sent to your email');
      setForgotStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Please enter complete OTP'); return; }
    setLoading(true);
    try {
      await verifyOTP(forgotEmail, otp);
      toast.success('OTP verified');
      setForgotStep('reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await resetPassword(forgotEmail, otp, newPassword);
      toast.success('Password reset successfully! Please login.');
      setActiveTab('login'); setForgotStep('email'); setForgotEmail(''); setOtp(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  const renderForgotPassword = () => {
    if (forgotStep === 'email') {
      return (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input id="forgot-email" type="email" placeholder="your.email@united-group.in" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="pl-10" required data-testid="forgot-email-input" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={loading} data-testid="send-otp-btn">
            {loading ? 'Sending...' : 'Send OTP'}
          </Button>
        </form>
      );
    }
    if (forgotStep === 'otp') {
      return (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <div className="space-y-2 text-center">
            <Label>Enter OTP sent to {forgotEmail}</Label>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup>
                  <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                  <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>
          <Button type="submit" className="w-full bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={loading} data-testid="verify-otp-btn">
            {loading ? 'Verifying...' : 'Verify OTP'}
          </Button>
        </form>
      );
    }
    return (
      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pl-10 pr-10" required data-testid="new-password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input id="confirm-password" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10" required data-testid="confirm-password-input" />
          </div>
        </div>
        <Button type="submit" className="w-full bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={loading} data-testid="reset-password-btn">
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </form>
    );
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Project Management Visual */}
      <div className="hidden lg:flex lg:w-[55%] flex-col relative overflow-hidden bg-[#0B1120]">
        {/* Background layers */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(245,166,35,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(245,166,35,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(245,166,35,0.06)_0%,transparent_70%)]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(59,130,246,0.04)_0%,transparent_70%)]" />
        </div>
        
        <div className="relative z-10 flex flex-col h-full px-10 py-8">
          {/* Logo */}
          <div className="mb-auto">
            <img src={DARK_LOGO} alt="United Group" className="h-10 w-auto opacity-90" />
          </div>
          
          {/* Center Content */}
          <div className="flex-1 flex flex-col justify-center max-w-lg">
            <div className="mb-2">
              <span className="text-[#F5A623] text-xs font-semibold tracking-[0.2em] uppercase">Project Management</span>
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-4">
              Manage work<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5A623] to-[#FFD080]">across departments</span>
            </h1>
            <p className="text-white/45 text-base leading-relaxed mb-8 max-w-md">
              Assign tasks, track progress, and collaborate with your team in Marketing, Travel, and HR Management — all in one place.
            </p>
            
            {/* Kanban Board */}
            <KanbanBoard />
            
            {/* Stats Row */}
            <div className="flex items-center gap-8 mt-8">
              <div>
                <p className="text-2xl font-bold text-white"><CountUp end={3} /></p>
                <p className="text-white/35 text-xs">Departments</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <p className="text-2xl font-bold text-white"><CountUp end={4} /></p>
                <p className="text-white/35 text-xs">Role Levels</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <p className="text-2xl font-bold text-[#F5A623]"><CountUp end={100} suffix="%" /></p>
                <p className="text-white/35 text-xs">Task Visibility</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <MiniChart />
              </div>
            </div>
            
            {/* Features */}
            <div className="mt-8 space-y-3">
              <FeatureItem title="AI-Powered Insights" desc="Smart suggestions, delay prediction, and natural language search" delay={0.2} />
              <FeatureItem title="Role-Based Access" desc="Super Admin, Admin, Coordinator & Member with strict permissions" delay={0.4} />
              <FeatureItem title="Real-Time Tracking" desc="Kanban boards, calendar view, and workload management" delay={0.6} />
            </div>
          </div>
          
          {/* Bottom */}
          <div className="mt-auto pt-6">
            <p className="text-white/20 text-xs">United Group &middot; Uni-Work Platform</p>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-[45%] flex flex-col items-center justify-center p-6 sm:p-8 bg-background relative overflow-hidden">
        {/* Subtle background for right panel */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(245,166,35,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(245,166,35,0.015)_1px,transparent_1px)] bg-[size:32px_32px] dark:bg-[linear-gradient(rgba(245,166,35,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(245,166,35,0.03)_1px,transparent_1px)]" />
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[radial-gradient(circle,rgba(245,166,35,0.06)_0%,transparent_60%)]" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-[radial-gradient(circle,rgba(59,130,246,0.04)_0%,transparent_60%)]" />
        </div>

        <button 
          onClick={toggleTheme}
          className="absolute top-6 right-6 p-2 rounded-lg hover:bg-accent transition-colors z-10"
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        
        {/* Mobile Header */}
        <div className="lg:hidden mb-6 flex flex-col items-center relative z-10">
          <img src={theme === 'dark' ? DARK_LOGO : LIGHT_LOGO} alt="United Group" className="w-40 mb-3" />
          <h1 className="text-2xl font-heading font-bold mb-1">Uni-Work</h1>
          <p className="text-muted-foreground text-sm text-center max-w-xs mb-4">Streamline project management across departments</p>
          
          {/* Mini Kanban Illustration for mobile */}
          <div className="w-full max-w-xs mb-4">
            <svg viewBox="0 0 320 70" fill="none" className="w-full login-kanban">
              <rect x="2" y="2" width="316" height="66" rx="10" className="fill-[#0B1120] dark:fill-slate-900/80" stroke="rgba(245,166,35,0.12)" strokeWidth="0.5"/>
              {/* Column headers */}
              <rect x="10" y="8" width="94" height="16" rx="4" fill="rgba(100,116,139,0.3)"/>
              <text x="57" y="19" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="7" fontWeight="600">To Do</text>
              <rect x="113" y="8" width="94" height="16" rx="4" fill="rgba(59,130,246,0.25)"/>
              <text x="160" y="19" textAnchor="middle" fill="rgba(147,197,253,0.9)" fontSize="7" fontWeight="600">In Progress</text>
              <rect x="216" y="8" width="94" height="16" rx="4" fill="rgba(34,197,94,0.2)"/>
              <text x="263" y="19" textAnchor="middle" fill="rgba(134,239,172,0.9)" fontSize="7" fontWeight="600">Completed</text>
              {/* Cards row */}
              <rect x="10" y="30" width="94" height="28" rx="4" fill="rgba(255,255,255,0.06)" className="login-card-anim login-card-1"/>
              <rect x="16" y="36" width="40" height="3" rx="1.5" fill="rgba(245,166,35,0.5)"/>
              <rect x="16" y="43" width="60" height="2" rx="1" fill="rgba(255,255,255,0.12)"/>
              <rect x="16" y="49" width="45" height="2" rx="1" fill="rgba(255,255,255,0.08)"/>
              <circle cx="94" cy="42" r="5" fill="rgba(239,68,68,0.2)"/>
              <text x="94" y="44" textAnchor="middle" fill="rgba(239,68,68,0.7)" fontSize="5" fontWeight="700">1</text>
              
              <rect x="113" y="30" width="94" height="28" rx="4" fill="rgba(59,130,246,0.08)" className="login-card-anim login-card-4"/>
              <rect x="119" y="36" width="45" height="3" rx="1.5" fill="rgba(59,130,246,0.5)"/>
              <rect x="119" y="43" width="65" height="2" rx="1" fill="rgba(255,255,255,0.12)"/>
              <rect x="119" y="50" width="70" height="2" rx="1" fill="rgba(255,255,255,0.05)"/>
              <rect x="119" y="50" width="42" height="2" rx="1" fill="rgba(59,130,246,0.5)" className="login-progress-anim"/>
              
              <rect x="216" y="30" width="94" height="28" rx="4" fill="rgba(34,197,94,0.06)" className="login-card-anim login-card-6"/>
              <rect x="222" y="36" width="50" height="3" rx="1.5" fill="rgba(34,197,94,0.4)"/>
              <rect x="222" y="43" width="58" height="2" rx="1" fill="rgba(255,255,255,0.1)"/>
              <circle cx="300" cy="42" r="5" fill="rgba(34,197,94,0.2)"/>
              <path d="M297 42l2 2 4-4.5" stroke="rgba(34,197,94,0.7)" strokeWidth="1" fill="none" strokeLinecap="round"/>
            </svg>
          </div>
          
          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-2">
            <span className="px-3 py-1 bg-[#F5A623]/8 border border-[#F5A623]/15 rounded-full text-xs text-muted-foreground">Task Management</span>
            <span className="px-3 py-1 bg-blue-500/8 border border-blue-500/15 rounded-full text-xs text-muted-foreground">AI Insights</span>
            <span className="px-3 py-1 bg-emerald-500/8 border border-emerald-500/15 rounded-full text-xs text-muted-foreground">Team Tracking</span>
          </div>
        </div>
        
        <Card className="w-full max-w-md border-border/50 shadow-xl relative z-10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-heading">
              {activeTab === 'forgot' ? 'Reset Password' : 'Welcome'}
            </CardTitle>
            <CardDescription>
              {activeTab === 'forgot' ? 'Enter your email to receive OTP' : 'Sign in to your account or create a new one'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeTab === 'forgot' ? (
              <div className="space-y-4">
                <button onClick={() => { setActiveTab('login'); setForgotStep('email'); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login">
                  <ArrowLeft className="h-4 w-4" /> Back to login
                </button>
                {renderForgotPassword()}
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="email" type="email" placeholder="your.email@united-group.in" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} className="pl-10" required data-testid="login-email-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="password" type={showPassword ? 'text' : 'password'} value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} className="pl-10 pr-10" required data-testid="login-password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 rounded border-border accent-[#F5A623]"
                          data-testid="remember-me-checkbox"
                        />
                        <span className="text-sm text-muted-foreground">Keep me logged in <span className="text-xs">(30 days)</span></span>
                      </label>
                      <button type="button" onClick={() => setActiveTab('forgot')} className="text-sm text-[#F5A623] hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </button>
                    </div>
                    <Button type="submit" className="w-full bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={loading} data-testid="login-submit-btn">
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="signup-name" placeholder="John Doe" value={signupData.full_name} onChange={(e) => setSignupData({ ...signupData, full_name: e.target.value })} className="pl-10" required data-testid="signup-name-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="signup-email" type="email" placeholder="your.email@united-group.in" value={signupData.email} onChange={(e) => setSignupData({ ...signupData, email: e.target.value })} className="pl-10" required data-testid="signup-email-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-department">Department</Label>
                      <Select value={signupData.department} onValueChange={(v) => setSignupData({ ...signupData, department: v })} required>
                        <SelectTrigger data-testid="signup-department-select">
                          <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEPARTMENTS.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-role">Role</Label>
                      <Select value={signupData.role} onValueChange={(v) => setSignupData({ ...signupData, role: v })}>
                        <SelectTrigger data-testid="signup-role-select">
                          <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(role => (<SelectItem key={role} value={role}>{role}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="signup-password" type={showPassword ? 'text' : 'password'} value={signupData.password} onChange={(e) => setSignupData({ ...signupData, password: e.target.value })} className="pl-10 pr-10" required data-testid="signup-password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={loading} data-testid="signup-submit-btn">
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
        
        <p className="mt-6 text-sm text-muted-foreground relative z-10">
          Only @united-group.in email addresses are accepted
        </p>
      </div>
    </div>
  );
};
